/**
 * Authentication.
 *
 * The backend is phone-OTP only: no password, no SSO, no MFA beyond the code.
 * `skipAuth` is set on every call here — these endpoints must never trigger the
 * client's refresh-and-replay, or a failing refresh would recurse.
 */

import { api, paths } from './client'
import type {
  Bootstrap,
  OtpChallenge,
  OtpVerifyResponse,
  TokenBundle,
  UserMe,
} from '@/types/api'

export interface DeviceInfo {
  id: string
  platform: 'web'
  app_version: string
}

/** Stable per-browser id so the backend can tell sessions apart. */
export function deviceInfo(): DeviceInfo {
  const KEY = 'tf.device-id'
  let id: string | null = null
  try {
    id = localStorage.getItem(KEY)
    if (!id) {
      id = `web-${crypto.randomUUID()}`
      localStorage.setItem(KEY, id)
    }
  } catch {
    // Private mode or blocked storage: a per-load id still works.
    id = `web-${crypto.randomUUID()}`
  }
  return { id, platform: 'web', app_version: '1.0.0' }
}

export const authApi = {
  requestOtp: (phone: string) =>
    api.post<OtpChallenge>(paths.v1('/auth/otp'), { phone }, { skipAuth: true }),

  verifyOtp: (challengeId: string, code: string) =>
    api.post<OtpVerifyResponse>(
      paths.v1('/auth/otp/verify'),
      { challenge_id: challengeId, code, device: deviceInfo() },
      { skipAuth: true },
    ),

  selectTenant: (preAuthToken: string, tenantId: string) =>
    api.post<TokenBundle>(
      paths.v1('/auth/select-tenant'),
      { pre_auth_token: preAuthToken, tenant_id: tenantId },
      { skipAuth: true },
    ),

  /** Mid-session switch. Requires a live access token; issues a whole new pair. */
  switchTenant: (tenantId: string) =>
    api.post<TokenBundle>(paths.v1('/auth/switch-tenant'), { tenant_id: tenantId }),

  refresh: (refreshToken: string) =>
    api.post<TokenBundle>(paths.v1('/auth/refresh'), { refresh_token: refreshToken }, {
      skipAuth: true,
    }),

  logout: (refreshToken: string) =>
    api.post<void>(paths.v1('/auth/logout'), { refresh_token: refreshToken }, { skipAuth: true }),

  me: () => api.get<UserMe>(paths.v1('/me')),

  bootstrap: () => api.get<Bootstrap>(paths.v1('/bootstrap')),
}
